import React, { useEffect } from 'react';
import IntervalLabel from './IntervalLabel';
import { Checkbox, Tooltip } from 'antd';
import { SliceIntervalWrapper, SlicePositionWrapper } from './styled';
import {
  TABLE_COMPARE_COLOR,
  TABLE_COMPARE_OPACITY,
  getCompareIntervalLabel,
  getCompareTooltipText,
} from './utils';
import {
  chartIntervalSelector,
  tableCompareIntervalSelector,
  noBrushSelector,
} from '@store/trends/birdChart/selectors';
import { clearMetricFilter } from '@store/trends/base/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useAtom, useAtomValue } from 'jotai';
import { dodAtom } from '../metrics-panel-hz/atoms';
import { isEiModule, isAdModule, isAppModule } from '@utils/constants';
import { track } from '@utils';
import { isValidInterval } from '@src/components/summary-panel/SummaryContainer/utils/dates';
import {
  filterColorSelector,
  realTimeModeOnTrendsSelector,
  dataAccessDurationSelector,
} from '@src/store/common/selectors';
import { Tabs } from '@store/trends/base/reducer';
import { isTableCompareEnabledAtom } from './atoms';

const intervalTooltipText =
  'Reflects the sliced range from the time-series chart — a subset of the selected period.';

function TableCompare({ activeTab }) {
  const dispatch = useDispatch();
  const interval = useSelector(chartIntervalSelector);
  const [isTableCompareEnabled, tableCompareToggle] = useAtom(isTableCompareEnabledAtom);
  const compareInterval: any = useSelector(tableCompareIntervalSelector);
  const isLiveMode: boolean = useSelector(realTimeModeOnTrendsSelector);
  const durationAllowed = useSelector(dataAccessDurationSelector);

  const dodEnabled = useAtomValue(dodAtom);
  const isSliceAvailable = !useSelector(noBrushSelector);

  const compareIntervalLabel = getCompareIntervalLabel({
    isSliceAvailable,
    dodEnabled,
    compareInterval,
  });
  const shouldShowCompareToggle =
    (isEiModule || isAdModule || isAppModule) && !isLiveMode && activeTab === Tabs.TopTrends;

  const filterColor = useSelector(filterColorSelector);
  const isValidCompareInterval = isValidInterval(
    compareInterval,
    isAppModule ? durationAllowed : undefined,
  );

  const isCompareToggleEnabled = (isSliceAvailable || dodEnabled) && isValidCompareInterval;
  const tooltipText = getCompareTooltipText({
    isCompareToggleEnabled: isSliceAvailable || dodEnabled,
    isValidCompareInterval,
    compareInterval,
  });

  const onChange = () => {
    const isEnabledFlag = !isTableCompareEnabled;
    if (isEnabledFlag) {
      dispatch(clearMetricFilter());

      const trigger = isSliceAvailable ? 'slice' : 'dod';
      track({
        event: 'TABLE_COMPARE_CLICK',
        data: {
          enabled: isTableCompareEnabled,
          trigger,
        },
      });
    }
    tableCompareToggle(isEnabledFlag);
  };

  useEffect(() => {
    if (!dodEnabled && !isSliceAvailable) {
      // https://conviva.atlassian.net/browse/CE-10849
      // After we uncheck the date compare checkbox, we should automatically cancel the table compare
      tableCompareToggle(false);
    }
  }, [dodEnabled, tableCompareToggle, isSliceAvailable]);

  return (
    <SlicePositionWrapper>
      {shouldShowCompareToggle && (
        <>
          <SliceIntervalWrapper color={TABLE_COMPARE_COLOR}>
            <div
              style={{
                width: 'fit-content',
                textAlign: 'center',
                alignItems: 'center',
                display: 'flex',
                paddingLeft: '5px',
              }}
            >
              <Tooltip title={tooltipText}>
                <Checkbox
                  checked={isTableCompareEnabled}
                  disabled={!isCompareToggleEnabled}
                  onChange={onChange}
                  style={{ fontSize: 12 }}
                >
                  {`${compareIntervalLabel}`}
                </Checkbox>
              </Tooltip>
            </div>
          </SliceIntervalWrapper>
          <div style={{ padding: '5px' }}>vs</div>
        </>
      )}
      <SliceIntervalWrapper color={filterColor + TABLE_COMPARE_OPACITY}>
        <Tooltip title={intervalTooltipText}>
          <div style={{ width: 'fit-content', textAlign: 'center' }}>
            <IntervalLabel interval={interval} />
          </div>
        </Tooltip>
      </SliceIntervalWrapper>
    </SlicePositionWrapper>
  );
}

export default TableCompare;
